import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useNavigate } from "react-router-dom";
import { usePageContext } from "../../context/PageContext";
import { useChatbots } from "../../hooks/useChatbots";
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import GenericTable from "../../components/GenericTable";
import UserDeletionDialog from "../Settings/Users/UserDeletionDialog";
import { useTranslation } from "react-i18next";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR");
}

const ChatbotsList = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { setCurrentPage } = usePageContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [errorMessage, setErrorMessage] = useState(null);

  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);
  const canCreate = can(scopeSet, "chatbot", "create");
  const canUpdate = can(scopeSet, "chatbot", "update");
  const canDelete = can(scopeSet, "chatbot", "delete");

  const {
    data: botUsers = [],
    error,
    refetch,
    deleteMutation,
    updateMutation,
    isLoading,
    isFetching,
  } = useChatbots();

  useEffect(() => {
    setCurrentPage({ title: t("chatbotConfigPage.title"), showBackButton: false });
  }, [setCurrentPage, t]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error?.message || t("chatbotsListPage.loadError"));
    } else {
      setErrorMessage(null);
    }
  }, [error, t]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return botUsers;
    return botUsers.filter((u) => {
      const nome = String(u?.nome ?? "").toLowerCase();
      const email = String(u?.email ?? "").toLowerCase();
      return nome.includes(q) || email.includes(q);
    });
  }, [botUsers, search]);

  const rows = useMemo(
    () =>
      filtered.map((u) => {
        const st = u.status ?? u.ativo;
        const active = st === 1 || st === true || st === "1";
        return {
          id: u.id,
          name: u.nome ?? "—",
          status: active,
          updatedAt: u.updatedAt ?? u.updated_at ?? u.data_ultima_alteracao,
        };
      }),
    [filtered]
  );

  const handleToggleStatus = useCallback(
    (row) => {
      const nextActive = !row.status;
      updateMutation.mutate(
        { userId: row.id, active: nextActive },
        {
          onSuccess: () => {
            setSnack({ open: true, severity: "success", message: t("agentsListPage.snackStatusUpdated") });
            refetch();
          },
          onError: (err) => {
            setSnack({
              open: true,
              severity: "error",
              message: err?.message || t("agentsListPage.snackStatusError"),
            });
          },
        }
      );
    },
    [updateMutation, refetch, t]
  );

  const handleDeleteChatbot = useCallback(
    (id) => {
      const row = botUsers.find((u) => String(u.id) === String(id));
      setConfirmDelete({ id, nome: row?.nome || row?.name || "" });
      setErrorMessage(null);
    },
    [botUsers]
  );

  const columns = useMemo(
    () => [
      { field: "name", headerName: t("shared.name"), flex: 1, minWidth: 200 },
      {
        field: "status",
        headerName: t("shared.status"),
        width: 160,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              size="small"
              checked={!!params.row.status}
              disabled={!canUpdate || updateMutation.isLoading}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                handleToggleStatus(params.row);
              }}
            />
            <Chip
              size="small"
              label={params.row.status ? t("shared.active") : t("shared.inactive")}
              color={params.row.status ? "success" : "default"}
              variant="outlined"
            />
          </Stack>
        ),
      },
      {
        field: "updatedAt",
        headerName: t("chatbotsListPage.columnLastUpdate"),
        width: 180,
        renderCell: (p) => formatDate(p.row.updatedAt),
      },
      {
        field: "actions",
        headerName: t("shared.actions"),
        width: 140,
        renderCell: (p) => (
          <Box display="flex" gap={0.5}>
            <Tooltip title={t("agentsListPage.analytics")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/chatbots/${p.row.id}/analytics`);
                }}
              >
                <BarChartRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canUpdate && (
              <Tooltip title={t("shared.edit")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/chatbots/${p.row.id}`);
                  }}
                >
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t("shared.delete")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteChatbot(p.row.id);
                  }}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, canUpdate, canDelete, navigate, handleToggleStatus, handleDeleteChatbot, updateMutation.isLoading]
  );

  const handleConfirmDelete = (resolutions) => {
    if (!confirmDelete?.id) return;
    setIsProcessing(true);
    deleteMutation.mutate(
      { id: confirmDelete.id, resolutions },
      {
        onSuccess: () => {
          setSnack({ open: true, severity: "success", message: t("chatbotsListPage.snackDeleted") });
          setConfirmDelete(null);
          setErrorMessage(null);
          refetch();
        },
        onError: (err) => {
          const message =
            err?.response?.data?.message || err?.message || t("chatbotsListPage.snackDeleteError");
          setErrorMessage(message);
          setSnack({ open: true, severity: "error", message });
        },
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const handleDeleteSelected = (ids) => {
    if (!ids?.length) return;
    if (ids.length > 1) {
      setErrorMessage(t("chatbotsListPage.deleteOneAtATime"));
      return;
    }
    handleDeleteChatbot(ids[0]);
  };

  return (
    <Box className="tour-chatbots-root" sx={{ paddingTop: 1 }}>
      <Box
        className="tour-chatbots-header"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 1,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("chatbotsListPage.heading")}
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/chatbots/new")}
            fullWidth={isMobile}
          >
            {t("chatbotsListPage.createButton")}
          </Button>
        )}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <Paper className="tour-chatbots-table">
        <GenericTable
          rows={rows}
          columns={columns}
          rowCount={rows.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(0);
          }}
          onSearch={(term) => {
            setSearch(term);
            setPage(0);
          }}
          isLoading={isLoading || isFetching}
          onExportPDFTitle={t("chatbotsListPage.exportSelectedTitle")}
          exportFileName="chatbots"
          onDeleteSelected={canDelete ? handleDeleteSelected : undefined}
          onRowClick={(row) => navigate(`/chatbots/${row.id}`)}
          paginationMode="client"
        />
      </Paper>

      <UserDeletionDialog
        open={Boolean(confirmDelete)}
        user={confirmDelete}
        candidates={[]}
        submitting={isProcessing}
        errorMessage={errorMessage}
        title={t("chatbotsListPage.deleteDialogTitle")}
        confirmLabel={t("chatbotsListPage.deleteDialogConfirm")}
        onClose={() => {
          if (!isProcessing) {
            setConfirmDelete(null);
            setErrorMessage(null);
          }
        }}
        onConfirm={handleConfirmDelete}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default ChatbotsList;
